import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useLang } from "@/contexts/LangContext";

import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";


export default function Checkout() {
    const { cart, refresh } = useCart();
    const { t, lang } = useLang();
    const navigate = useNavigate();

    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [shippingConfig, setShippingConfig] = useState(null);

    const [form, setForm] = useState({
        address: "",
        city: "",
        phone: "",
        notes: "",
        coupon_code: "",
    });

    useEffect(() => {
        api.get("/cities")
            .then((response) => {
                setCities(response.data);
            })
            .catch(() => {
                toast.error(
                    lang === "ar"
                        ? "تعذر تحميل قائمة المدن"
                        : "Could not load cities"
                );
            });
    }, [lang]);

    useEffect(() => {
        api.get("/shipping/config")
            .then((response) => {
                setShippingConfig(response.data);
            })
            .catch(() => {
                toast.error(
                    lang === "ar"
                        ? "تعذر تحميل رسوم الشحن"
                        : "Could not load shipping fee"
                );
            });
    }, [lang]);

    const subtotal = Number(
        cart?.subtotal || 0
    );

    const includedProductVat = Number(
        cart?.vat_amount || 0
    );

    const shipmentCount = new Set(
        (cart?.items || [])
            .map((item) => item.store_id)
            .filter(Boolean)
    ).size;

    const shippingFeePerStore = Number(
        shippingConfig?.fee_per_store ?? 0
    );

    const shippingTotal = Number(
        (
            shipmentCount
            * shippingFeePerStore
        ).toFixed(2)
    );

    const totalBeforeDiscount = Number(
        (
            subtotal
            + shippingTotal
        ).toFixed(2)
    );

    const onSubmit = async (event) => {
        event.preventDefault();

        if (!form.city) {
            toast.error(
                lang === "ar"
                    ? "يرجى اختيار المدينة"
                    : "Please select a city"
            );
            return;
        }

        setLoading(true);

        try {
            const { data } = await api.post(
                "/checkout",
                form
            );

            await refresh();

            toast.success(
                lang === "ar"
                    ? "تم إنشاء الطلب. أكمل الدفع عبر ميسر."
                    : "Order created. Complete your payment with Moyasar."
            );

            navigate(
                `/payment/${data.order_id}`,
                {
                    replace: true,
                }
            );

        } catch (error) {
            const apiError = formatApiError(
                error.response?.data?.detail
            );

            const message = (
                apiError
                === "Tax configuration is incomplete for checkout"
            )
                ? (
                    lang === "ar"
                        ? "تعذر إتمام الطلب مؤقتًا بسبب عدم اكتمال البيانات الضريبية للبائع. يرجى المحاولة لاحقًا أو استخدام متجر آخر."
                        : (
                            "We cannot complete this order temporarily "
                            + "because the seller's tax information is "
                            + "incomplete. Please try again later or "
                            + "use another store."
                        )
                )
                : (
                    apiError
                    || error.message
                );

            toast.error(
                message
            );

        } finally {
            setLoading(false);
        }
    };

    if (!cart?.items?.length) {
        return (
            <Layout>
                <div className="max-w-md mx-auto py-20 text-center">
                    <p className="text-muted-foreground">
                        {t("emptyCart")}
                    </p>

                    <Button
                        onClick={() =>
                            navigate("/products")
                        }
                        className="mt-6 rounded-full bg-terracotta text-white"
                    >
                        {t("exploreProducts")}
                    </Button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">

                <h1 className="text-3xl md:text-4xl font-black text-espresso">
                    {t("checkout")}
                </h1>

                <form
                    onSubmit={onSubmit}
                    className="mt-8 grid md:grid-cols-3 gap-8"
                    data-testid="checkout-form"
                >
                    <div className="md:col-span-2 space-y-4">

                        <div className="bg-white border border-border rounded-2xl p-6 space-y-4">

                            <div>
                                <Label htmlFor="address">
                                    {t("address")}
                                </Label>

                                <Textarea
                                    id="address"
                                    required
                                    value={form.address}
                                    onChange={(event) =>
                                        setForm({
                                            ...form,
                                            address:
                                                event.target.value,
                                        })
                                    }
                                    data-testid="address-input"
                                    className="mt-1.5"
                                    rows={2}
                                />
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">

                                <div>
                                    <Label>
                                        {t("city")}
                                    </Label>

                                    <Select
                                        value={form.city}
                                        onValueChange={(value) =>
                                            setForm({
                                                ...form,
                                                city: value,
                                            })
                                        }
                                    >
                                        <SelectTrigger
                                            data-testid="city-select"
                                            className="mt-1.5"
                                        >
                                            <SelectValue
                                                placeholder={
                                                    t("city")
                                                }
                                            />
                                        </SelectTrigger>

                                        <SelectContent>
                                            {cities.map(
                                                (city) => (
                                                    <SelectItem
                                                        key={
                                                            city.city_id
                                                        }
                                                        value={
                                                            city.slug
                                                        }
                                                    >
                                                        {
                                                            lang
                                                            === "ar"
                                                                ? city.name_ar
                                                                : city.name_en
                                                        }
                                                    </SelectItem>
                                                )
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div>
                                    <Label htmlFor="checkout-phone">
                                        {t("phone")}
                                    </Label>

                                    <Input
                                        id="checkout-phone"
                                        required
                                        value={form.phone}
                                        onChange={(event) =>
                                            setForm({
                                                ...form,
                                                phone:
                                                    event.target.value,
                                            })
                                        }
                                        data-testid="phone-input"
                                        className="mt-1.5"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="notes">
                                    {t("notes")}
                                </Label>

                                <Textarea
                                    id="notes"
                                    value={form.notes}
                                    onChange={(event) =>
                                        setForm({
                                            ...form,
                                            notes:
                                                event.target.value,
                                        })
                                    }
                                    rows={2}
                                    className="mt-1.5"
                                    data-testid="notes-input"
                                />
                            </div>

                            <div>
                                <Label htmlFor="coupon">
                                    {t("coupon")}
                                </Label>

                                <Input
                                    id="coupon"
                                    value={form.coupon_code}
                                    onChange={(event) =>
                                        setForm({
                                            ...form,
                                            coupon_code:
                                                event.target.value
                                                    .toUpperCase(),
                                        })
                                    }
                                    className="mt-1.5"
                                    data-testid="coupon-input"
                                />
                            </div>

                        </div>
                    </div>

                    <div>
                        <div className="bg-white border border-border rounded-2xl p-6 sticky top-24">

                            <div className="text-sm space-y-2">

                                {cart.items.map(
                                    (item) => (
                                        <div
                                            key={
                                                item.product_id
                                            }
                                            className="flex justify-between"
                                        >
                                            <span className="text-muted-foreground truncate me-2">
                                                {item.title}
                                                {" × "}
                                                {item.quantity}
                                            </span>

                                            <span className="font-bold text-espresso whitespace-nowrap">
                                                {Number(
                                                    item.line_total
                                                ).toFixed(2)}
                                            </span>
                                        </div>
                                    )
                                )}

                                <div className="border-t border-border pt-2 flex justify-between">
                                    <span className="text-muted-foreground">
                                        {
                                            lang === "ar"
                                                ? "\u0627\u0644\u0645\u062c\u0645\u0648\u0639 \u0627\u0644\u0641\u0631\u0639\u064a (\u0634\u0627\u0645\u0644 \u0627\u0644\u0636\u0631\u064a\u0628\u0629)"
                                                : "Subtotal (VAT included)"
                                        }
                                    </span>

                                    <span className="font-bold text-espresso">
                                        {subtotal.toFixed(2)}
                                        {" "}
                                        {t("SAR")}
                                    </span>
                                </div>

                                                                {
                                    includedProductVat > 0
                                    && (
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">
                                                {
                                                    lang === "ar"
                                                        ? "\u0645\u0646\u0647\u0627 \u0636\u0631\u064a\u0628\u0629 \u0627\u0644\u0642\u064a\u0645\u0629 \u0627\u0644\u0645\u0636\u0627\u0641\u0629 (15%)"
                                                        : "Includes VAT (15%)"
                                                }
                                            </span>

                                            <span
                                                className="font-bold text-espresso"
                                                data-testid="vat-amount"
                                            >
                                                {
                                                    includedProductVat.toFixed(
                                                        2
                                                    )
                                                }
                                                {" "}
                                                {t("SAR")}
                                            </span>
                                        </div>
                                    )
                                }

                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">
                                        {lang === "ar"
                                            ? `الشحن (${shipmentCount} ${shipmentCount === 1 ? "شحنة" : "شحنات"})`
                                            : `Shipping (${shipmentCount} shipment${shipmentCount === 1 ? "" : "s"})`}
                                    </span>

                                    <span
                                        className="font-bold text-espresso"
                                        data-testid="shipping-total"
                                    >
                                        {shippingConfig
                                            ? shippingTotal.toFixed(2)
                                            : "..."}
                                        {" "}
                                        {t("SAR")}
                                    </span>
                                </div>



                                <div className="border-t border-border pt-3 mt-2 flex justify-between text-base">
                                    <span className="font-black text-espresso">
                                        {form.coupon_code
                                            ? (lang === "ar"
                                                ? "الإجمالي قبل خصم الكوبون"
                                                : "Total before coupon")
                                            : t("total")}
                                    </span>

                                    <span
                                        className="font-black text-terracotta"
                                        data-testid="checkout-total"
                                    >
                                        {shippingConfig
                                            ? totalBeforeDiscount.toFixed(2)
                                            : "..."}
                                        {" "}
                                        {t("SAR")}
                                    </span>
                                </div>

                                {form.coupon_code && (
                                    <div className="text-xs text-muted-foreground">
                                        {lang === "ar"
                                            ? "سيتم احتساب خصم الكوبون عند إنشاء الطلب."
                                            : "Coupon discount and final VAT will be calculated when the order is created."}
                                    </div>
                                )}
                            </div>

                            <Button
                                type="submit"
                                disabled={loading || !shippingConfig}
                                className="w-full mt-5 rounded-full h-12 bg-terracotta hover:bg-terracotta/90 text-white font-bold"
                                data-testid="place-order-btn"
                            >
                                {loading
                                    ? "..."
                                    : lang === "ar"
                                        ? "المتابعة للدفع"
                                        : "Continue to payment"
                                }
                            </Button>

                            <div className="text-xs text-muted-foreground text-center mt-3">
                                {lang === "ar"
                                    ? "الدفع الإلكتروني الآمن عبر ميسر — مدى، Visa، Mastercard"
                                    : "Secure online payment via Moyasar — mada, Visa, Mastercard"
                                }
                            </div>

                        </div>
                    </div>

                </form>
            </div>
        </Layout>
    );
}